import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiHeader,
  EuiHeaderLink,
  EuiHeaderSection,
  EuiHeaderSectionItem,
  EuiHeaderSectionItemButton,
  EuiPageHeaderSection,
  EuiTitle,
  EuiCallOut,
  EuiIcon,
  EuiSelectable,
} from "@elastic/eui";
import moment from "moment-timezone";
import { Roles, Subjects } from "settings/permissions";
import { isNullOrUndefined, isSubjectAuthorizedForUser } from "utils";
import { DocsUrl } from "Consts";
import { FDTitle } from "components/Title/FDTitle";
import Tabs from "components/Tabs/TopTabs";
import Popover from "components/Popover/Popover";
import Button from "components/Buttons/Button";
import { NotificationsFlyout } from "./NotificationsFlyout";
import { HeaderUserMenu } from "./HeaderUserMenu";
import Emitter from "services/UtilsService/Emitter";
import { UserNotification } from "services/ServicesTypes/Clients";
import ClientService from "services/ClientService/Clients";
import WisdomAdvisor from "layout/WisdomAdvisor/WisdomAdvisor";
import { authSelector } from "reduxStore/auth/auth.selectors";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import { checkFilterResetRule } from "../PageUtils";
import { useTheme } from "../../../ThemeContext";
import "styles/global/page.css";

export const TopSection = ({
  navIsOpen,
  currentMenuItem,
  setIsMyProfileVisible,
  toggleSideNav,
  IsMenuItemAuthorized,
  sectionTitle,
  updateSectionTitle,
}: any) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toggleTheme, theme } = useTheme();
  const isAdvisorAllowed = isSubjectAuthorizedForUser({
    subject: Subjects.AskWisdom_Advisor,
    role: Roles.Read,
  });
  const { isLoggedIn } = useSelector(authSelector, shallowEqual);
  const { clientId, GlobalTopFilterObject } = useSelector(globalFilterSelector, shallowEqual);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [isNotificationsOpen, setNotificationsOpen] = useState<boolean>(false);
  const [isTabSelectorOpen, setTabSelectorOpen] = useState<boolean>(false);

  const redirect = (path: string) => {
    if (path) {
      navigate(path);
      //@ts-ignore
      Emitter.emit("pageUnload");
    }
  };

  const activeChildItem = currentMenuItem?.activeMenuItem ?? {};

  const isSingleWithSub =
    activeChildItem?.topNavLinks?.length === 1 &&
    activeChildItem?.topNavLinks.some((i: any) => i.subItems);

  const isParentSub = currentMenuItem?.activeTopNavItem?.label === "Overview" && isSingleWithSub;

  const tabLinks = isSingleWithSub
    ? activeChildItem.topNavLinks[0]?.subItems?.filter(
        (i: any) => i.path === window.location.pathname
      )
    : activeChildItem.topNavLinks || [];

  const mobileAppsIcon = (
    //@ts-ignore
    <EuiHeaderSectionItem border="right" className="header-apps">
      <EuiHeaderSectionItemButton
        aria-label="Toggle main navigation"
        data-test-subj="main-nav-toggle"
        onClick={toggleSideNav}>
        <EuiIcon size="m" type="menu" />
      </EuiHeaderSectionItemButton>
    </EuiHeaderSectionItem>
  );

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!clientId) return;
      try {
        const response = await ClientService.getUserNotifications(clientId);
        const sortedNotifications = (response?.notifications || []).sort(
          (a, b) => moment(b.createDateTime).valueOf() - moment(a.createDateTime).valueOf()
        );

        setNotifications(sortedNotifications);
      } catch (e: any) {
        console.error(e);
      }
    };

    fetchNotifications();
  }, [clientId]);

  const unread = notifications?.filter(
    (n) => n?.attributes?.isRead === false && n?.attributes?.isPopup !== true
  )?.length;

  const serviceNotifications = notifications?.filter((n) => n?.attributes?.isPopup !== true);
  const criticalNotification = notifications?.find(
    (n) => n?.attributes?.isPopup === true && n?.attributes?.isRead === false
  );

  const checkIsSelectedTab = (link: any) => {
    const isSubItems = !!link?.subItems;
    const parentPath = link.path;

    return isSubItems
      ? window.location.pathname.includes(parentPath)
      : link.path === window.location.pathname;
  };

  const handleLinkClick = (clientList: any, e: any, link: any) => {
    checkFilterResetRule(dispatch, GlobalTopFilterObject);
    redirect(link.path);
    updateSectionTitle(currentMenuItem?.activeSectionItem, link.path);
    setTabSelectorOpen(false);
  };

  const displayName = tabLinks?.find((i: any) => checkIsSelectedTab(i))?.label;

  const tabSelector = (
    <Tabs
      bottomBorder={false}
      items={tabLinks.filter((tl: any) =>
        IsMenuItemAuthorized({
          permissions: tl.permissions,
        })
      )}
      onTabClick={(link) => {
        checkFilterResetRule(dispatch, GlobalTopFilterObject);
        redirect(link.path);
        updateSectionTitle(currentMenuItem?.activeSectionItem, link.path);
      }}
      isTabSelected={checkIsSelectedTab}
      className="topNavTabs"
    />
  );

  return (
    <div>
      {!isNullOrUndefined(criticalNotification) && (
        <EuiCallOut
          title={criticalNotification?.category || ""}
          iconType="alert"
          style={{
            zIndex: 99999,
            position: "fixed",
            width: `calc(100% - ${navIsOpen ? "250px" : "0px"})`,
          }}
          onDismiss={() => {
            setNotifications(
              notifications?.map((i) => {
                if (
                  i?.id === criticalNotification?.id &&
                  criticalNotification?.attributes?.isPopup === true &&
                  criticalNotification?.attributes?.isRead === false
                ) {
                  return {
                    ...i,
                    attributes: {
                      ...i.attributes,
                      isRead: true,
                    },
                  };
                }
                return i;
              })
            );

            if (criticalNotification?.id) {
              ClientService.markUserNotificationAsRead(clientId, criticalNotification?.id).then(
                () => {}
              );
            }
          }}
          color="danger">
          <p>{criticalNotification?.message || ""}</p>
        </EuiCallOut>
      )}
      <EuiHeader
        position="fixed"
        style={{ paddingLeft: "16px", paddingRight: "10px", flexWrap: "wrap" }}>
        <EuiHeaderSection grow={false}>
          {/* @ts-ignore */}
          <EuiHeaderSectionItem border="right">{!navIsOpen && mobileAppsIcon}</EuiHeaderSectionItem>

          {activeChildItem.headerRenderFunc === undefined && (
            <EuiHeaderSectionItem
              className={!navIsOpen ? "clients-select-navclosed" : "clients-select-navopen"}>
              &nbsp;{" "}
              <EuiIcon color="var(--main-title)" size="m" type={sectionTitle.icon ?? "launch"} />
              &nbsp;&nbsp;
              <FDTitle title={sectionTitle.label} titleSize="xs" color="var(--main-title)" />
            </EuiHeaderSectionItem>
          )}
          {activeChildItem.headerRenderFunc && (
            <EuiHeaderSectionItem
              className={!navIsOpen ? "clients-select-navclosed" : "clients-select-navopen"}>
              {activeChildItem.headerRenderFunc()}
            </EuiHeaderSectionItem>
          )}

          <EuiPageHeaderSection>
            <EuiFlexGroup
              // wrap
              alignItems="center"
              className="topNav"
              gutterSize="s"
              justifyContent="center"
              responsive={false}
              style={{ zIndex: 1000 }}>
              {sectionTitle.label !== activeChildItem.label &&
                isNullOrUndefined(activeChildItem.topLabel) && (
                  <EuiFlexItem grow={false}>
                    <EuiIcon type="arrowRight" size="s" />
                  </EuiFlexItem>
                )}

              {!activeChildItem.headerRenderFunc && (
                <EuiFlexItem
                  grow={false}
                  onClick={() => {
                    redirect(activeChildItem.path);
                  }}>
                  <EuiTitle className="pageTitle" size="xs">
                    <h6> {activeChildItem.topLabel ?? activeChildItem.label}</h6>
                  </EuiTitle>
                </EuiFlexItem>
              )}

              {!activeChildItem.headerRenderFunc &&
                activeChildItem.topNavLinks &&
                activeChildItem.topNavLinks.length > 0 &&
                !isParentSub && (
                  <EuiFlexItem grow={false}>
                    <EuiIcon type="arrowRight" size="s" />
                  </EuiFlexItem>
                )}

              <EuiFlexItem style={{ margin: 0 }} className="max_tab_selector_wrapper">
                {tabSelector}
              </EuiFlexItem>

              <EuiFlexItem
                style={{ margin: 0 }}
                className={tabLinks?.length > 0 ? "min_tab_selector_wrapper" : "topNavTabs"}>
                {tabLinks?.length > 2 ? (
                  <div
                    className="minTabSelector topNavTabs"
                    style={{ display: "flex", alignItems: "center" }}>
                    <Popover
                      button={
                        <Button
                          type="empty"
                          onClick={() => setTabSelectorOpen(true)}
                          text={displayName}
                          iconType={"arrowDown"}
                          iconSide="right"
                          style={{
                            padding: "16px 8px ",
                            fontSize: "var(--fs14)",
                            fontWeight: 600,
                            color: "var(--button-bg) !important",
                            border: "1px solid var(--border-color)",
                            borderRadius: "8px",
                          }}
                        />
                      }
                      onClose={() => setTabSelectorOpen(false)}
                      isOpen={isTabSelectorOpen}
                      children={
                        <EuiSelectable
                          style={{ borderRadius: "var(--box-radius) !important" }}
                          onChange={handleLinkClick}
                          options={tabLinks
                            .filter((tl: any) =>
                              IsMenuItemAuthorized({
                                permissions: tl.permissions,
                              })
                            )
                            .map((link: any) => ({
                              label: link?.label,
                              id: link?.label,
                              path: link?.path,
                              showIcons: false,
                              onFocusBadge: false,
                              checked: checkIsSelectedTab(link) ? "on" : "",
                            }))}
                          singleSelection>
                          {(list) => <div style={{ width: 240 }}>{list}</div>}
                        </EuiSelectable>
                      }
                    />
                  </div>
                ) : (
                  tabSelector
                )}
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiPageHeaderSection>
        </EuiHeaderSection>

        <EuiHeaderSection side="right">
          {isLoggedIn && (
            <>
              {/* @ts-ignore */}
              <EuiHeaderSectionItem border="none" style={{ alignItems: "center" }}>
                {isAdvisorAllowed && <WisdomAdvisor />}
                <EuiHeaderSectionItemButton aria-label="mode" size="s" onClick={toggleTheme}>
                  <EuiIcon type={theme === "dark" ? "sun" : "moon"} size="m" />
                </EuiHeaderSectionItemButton>
                <EuiHeaderSectionItemButton
                  aria-label="notifications"
                  size="s"
                  onClick={() => setNotificationsOpen(!isNotificationsOpen)}
                  notification={unread}>
                  <EuiIcon type="bell" size="m" />
                </EuiHeaderSectionItemButton>
                {isNotificationsOpen && (
                  <NotificationsFlyout
                    onClose={() => setNotificationsOpen(false)}
                    notifications={serviceNotifications}
                    setNotifications={setNotifications}
                  />
                )}
                <EuiHeaderLink iconType="help" href={`${DocsUrl}`} target="_blank">
                  Help
                </EuiHeaderLink>
                <HeaderUserMenu setIsMyProfileVisible={setIsMyProfileVisible} />
              </EuiHeaderSectionItem>
              {/* @ts-ignore */}
              <EuiHeaderSectionItem border="none"></EuiHeaderSectionItem>
            </>
          )}
        </EuiHeaderSection>
      </EuiHeader>
      <div
        id="filter_target"
        className="filter-target"
        style={{ paddingRight: navIsOpen ? "270px" : "20px" }}
      />
    </div>
  );
};
